import { Camera, Upload } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { stockInApi } from '../Onboarding/Stock_in/stockInApi';
import { getAllSites } from '../SiteManagement/siteApi';
import { Button, Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input';
import { wasteLoggingApi } from './wasteLoggingApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const categoryOptions = ['Production waste', 'Service waste', 'Expiry waste'];

// Normalize category from API format to UI format
const normalizeCategoryFromAPI = (category) => {
  if (!category) return '';
  const normalized = category.toLowerCase();
  if (normalized === 'prod_waste') return 'Production waste';
  if (normalized === 'serv_waste') return 'Service waste';
  if (normalized === 'exp_waste') return 'Expiry waste';
  return category; // Return as is if already in UI format
};

const resolveSiteValue = (site) => {
  const value =
    site?.site_id ??
    site?.siteId ??
    site?.id ??
    site?._id ??
    site?.site_code ??
    site?.siteCode;
  return value !== undefined && value !== null ? String(value) : '';
};

const resolveSiteLabel = (site) =>
  site?.site_name ||
  site?.name ||
  site?.siteCode ||
  site?.site_code ||
  `Site ${site?.site_id ?? site?.id ?? ''}`;

const WasteLogging = () => {
  // Log screen view
  useScreenLogging('WasteLogging', {
    component: 'WasteLogging',
    screenType: 'waste-logging-form',
  });

  const location = useLocation();
  const editData = location.state || null;
  const isEditMode = editData?.editMode || false;

  const [logDate, setLogDate] = useState(editData?.logDate || getTodayDate());
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [selectedSite, setSelectedSite] = useState(editData?.siteId || '');

  const [stockItems, setStockItems] = useState([]);
  const [isStockItemsLoading, setIsStockItemsLoading] = useState(false);
  const [stockItemsError, setStockItemsError] = useState('');
  const [selectedItem, setSelectedItem] = useState(editData?.productName || '');
  const [selectedItemId, setSelectedItemId] = useState(editData?.stockItemId || '');
  const [selectedItemCurrentQuantity, setSelectedItemCurrentQuantity] = useState(null);
  const [isStockItem, setIsStockItem] = useState(editData?.isStockItem !== undefined ? editData.isStockItem : true);
  const [productName, setProductName] = useState(editData?.productName || '');
  const [quantity, setQuantity] = useState(editData?.quantity?.toString() || '');
  const [reason, setReason] = useState(editData?.reason || '');
  const [itemCost, setItemCost] = useState(editData?.itemCost?.toString() || '');
  const [category, setCategory] = useState(editData?.category ? normalizeCategoryFromAPI(editData.category) : '');
  const [wasteLogId, setWasteLogId] = useState(editData?.wasteLogId || null);

  const [feedback, setFeedback] = useState({ success: '', error: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [hasImage, setHasImage] = useState(false);
  const fileInputRef = useRef(null);

  // Camera functionality state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);

  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(
            error?.message || 'Unable to load sites. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };

    fetchSites();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch stock items - refetch when site changes
  useEffect(() => {
    let isMounted = true;

    const fetchStockItems = async () => {
      setIsStockItemsLoading(true);
      setStockItemsError('');
      try {
        // Build query parameters according to API documentation
        const params = {
          limit: 100,
          is_active: 'true', // Only get active items
          current_quantity_gt_zero: 'true', // Only show items with stock available
        };

        // Filter by site if one is selected
        if (selectedSite) {
          params.site_id = selectedSite;
        }

        const result = await stockInApi.listStockItems(params);
        if (result.success && result.items) {
          // Map items using the correct API response field names
          const items = result.items
            .map(item => ({
              id: item.stock_item_id || item.id || item._id,
              name: item.item_name ||'Unknown Item',
              stock_item_id: item.stock_item_id || item.id || item._id,
              current_quantity: item.current_quantity || 0,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          if (isMounted) {
            setStockItems(items);
          }
        } else {
          if (isMounted) {
            setStockItemsError(result.message || 'Unable to load stock items.');
          }
        }
      } catch (error) {
        if (isMounted) {
          setStockItemsError(
            error?.message || 'Unable to load stock items. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsStockItemsLoading(false);
        }
      }
    };

    fetchStockItems();

    return () => {
      isMounted = false;
    };
  }, [selectedSite]); // Refetch when site changes

  // Cleanup image preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Cleanup camera on component unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const siteOptions = useMemo(
    () =>
      sites
        .map((site) => ({
          value: resolveSiteValue(site),
          label: resolveSiteLabel(site),
        }))
        .filter((option) => option.value),
    [sites]
  );

  const estimatedLoss = useMemo(() => {
    const qty = parseInt(quantity, 10) || 0;
    const cost = parseFloat(itemCost) || 0;
    return qty * cost;
  }, [quantity, itemCost]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size before processing (10MB limit per API docs)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setFeedback({
        success: '',
        error: `Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 10MB.`,
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setFeedback({ success: '', error: 'Please select a valid image file.' });
      return;
    }

    try {
      setFeedback({ success: '', error: '' });

      // Store the file for later upload
      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setHasImage(true);
      };

      reader.onerror = () => {
        setFeedback({ success: '', error: 'Failed to read image file. Please try again.' });
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setFeedback({ success: '', error: 'Failed to process image. Please try again.' });
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageData(null);
    setImagePreview(null);
    setImageFile(null);
    setHasImage(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Check if camera is available
  const checkCameraAvailability = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          available: false,
          error: 'Camera not supported in this browser',
        };
      }

      // Check if any video input devices exist
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        device => device.kind === 'videoinput'
      );

      if (videoDevices.length === 0) {
        return { available: false, error: 'No camera found on this device' };
      }

      return { available: true, devices: videoDevices };
    } catch (err) {
      console.error('Error checking camera availability:', err);
      return { available: false, error: 'Unable to check camera availability' };
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraError('');
      setIsCameraActive(true);
      setShowCameraPreview(true);

      // First check camera availability
      const cameraCheck = await checkCameraAvailability();
      if (!cameraCheck.available) {
        throw new Error(cameraCheck.error);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Use front camera by default
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);

      // More specific error messages
      let errorMessage = 'Unable to access camera. Please check permissions.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (err.message.includes('not supported')) {
        errorMessage = 'Camera not supported in this browser.';
      } else if (err.message.includes('No camera found')) {
        errorMessage = 'No camera found on this device.';
      }

      setCameraError(errorMessage);
      setIsCameraActive(false);
      setShowCameraPreview(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setShowCameraPreview(false);
    setCameraError('');
  };

  // Camera capture
  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, 0, 0);

      // Convert to blob for upload
      canvas.toBlob((blob) => {
        if (blob) {
          setImageFile(blob);
          const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
          setImagePreview(photoDataUrl);
          setHasImage(true);
        }
      }, 'image/jpeg', 0.9);

      // Stop camera after taking photo
      stopCamera();
    } catch (err) {
      console.error('Error capturing photo:', err);
      setFeedback({ success: '', error: 'Failed to capture photo. Please try again.' });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleCameraCapture = () => {
    if (isCameraActive) {
      takePhoto();
    } else {
      startCamera();
    }
  };

  const quantityValue = parseInt(quantity, 10) || 0;
  const isQuantityValid = quantityValue > 0 && 
    (!isStockItem || selectedItemCurrentQuantity === null || quantityValue <= selectedItemCurrentQuantity);

  const isFormValid =
    !!logDate &&
    !!selectedSite &&
    ((isStockItem && !!selectedItemId) || (!isStockItem && (!!productName || !!selectedItem))) &&
    isQuantityValid &&
    reason.trim().length > 0 &&
    parseFloat(itemCost) >= 0 &&
    !!category;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid) {
      // Check for quantity validation error
      if (isStockItem && selectedItemCurrentQuantity !== null && quantityValue > selectedItemCurrentQuantity) {
        setFeedback({ 
          success: '', 
          error: `Quantity cannot exceed available stock (${selectedItemCurrentQuantity})` 
        });
      } else {
        setFeedback({ success: '', error: 'Please fill all required fields.' });
      }
      return;
    }

    setIsSubmitting(true);
    setFeedback({ success: '', error: '' });
    
    try {
      // Find selected site details
      const site = sites.find(s => resolveSiteValue(s) === selectedSite);
      const siteName = resolveSiteLabel(site);

      // Prepare waste data
      const wasteData = {
        waste_date: logDate,
        site_id: selectedSite,
        site_name: siteName,
        is_stock_item: isStockItem,
        quantity: parseInt(quantity, 10),
        cost_per_unit: parseFloat(itemCost) || 0,
        category: category,
        reason: reason.trim() || 'Not specified',
        notes: '', // Can be extended if needed
      };

      // Add stock item or product info
      if (isStockItem) {
        if (!selectedItemId) {
          throw new Error('Please select a stock item');
        }
        wasteData.stock_item_id = selectedItemId;
        // Get product name from selected item
        const selectedStockItem = stockItems.find(item => item.id === selectedItemId);
        if (selectedStockItem) {
          wasteData.product_name = selectedStockItem.name;
        }
      } else {
        // Non-stock item
        wasteData.product_name = productName || selectedItem;
        if (!wasteData.product_name) {
          throw new Error('Please enter a product name');
        }
      }

      // Log or update waste entry based on edit mode
      let result;
      let wasteId;
      
      if (isEditMode && wasteLogId) {
        // Update existing waste log
        result = await wasteLoggingApi.updateWaste(wasteLogId, wasteData);
        wasteId = wasteLogId;
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to update waste entry');
        }
      } else {
        // Create new waste log
        result = await wasteLoggingApi.logWaste(wasteData);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to log waste entry');
        }
        
        wasteId = result.data?.waste_id;
      }

      // Upload image if provided
      if (imageFile && wasteId) {
        try {
          // Get presigned URL
          const contentType = imageFile.type || 'image/jpeg';
          const presignResult = await wasteLoggingApi.presignWasteImageUpload(wasteId, contentType);
          
          if (presignResult.success) {
            // Upload image to S3
            const uploadResult = await wasteLoggingApi.uploadWasteImage(
              presignResult.data,
              imageFile
            );

            if (uploadResult.success) {
              // Update waste entry with image key (optional - API may auto-update)
              console.log('Image uploaded successfully:', uploadResult.imageKey);
            } else {
              console.warn('Image upload failed:', uploadResult.error);
              // Don't fail the whole operation if image upload fails
            }
          } else {
            console.warn('Failed to get presigned URL:', presignResult.error);
            // Don't fail the whole operation if presign fails
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          // Don't fail the whole operation if image upload fails
        }
      }

      setFeedback({ 
        success: isEditMode 
          ? 'Waste log updated successfully.' 
          : 'Waste log recorded successfully.', 
        error: '' 
      });

      // Reset form to initial state only if not in edit mode
      if (!isEditMode) {
        setLogDate(getTodayDate());
        setSelectedSite('');
        setSelectedItem('');
        setSelectedItemId('');
        setSelectedItemCurrentQuantity(null);
        setIsStockItem(true);
        setProductName('');
        setQuantity('');
        setReason('');
        setItemCost('');
        setCategory('');
        setWasteLogId(null);
        setImageData(null);
        setImagePreview(null);
        setImageFile(null);
        setHasImage(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting waste log:', error);
      setFeedback({ 
        success: '', 
        error: error.message || 'Failed to record waste log. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-10 text-center">
          <h1 className={typography.h2} style={{ color: '#000000' }}>
            {isEditMode ? 'Edit Waste Logging' : 'Waste Logging'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEditMode 
              ? 'Update waste log details below.' 
              : 'Track wastage across sites to monitor impact and take action.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Image Section */}
          <Card.Glassmorphism className="p-6">
            <div className="text-center">
              <h2
                className={typography.h2}
                style={{ color: '#000000', marginBottom: '1.5rem' }}
              >
                Wastage Image (Optional)
              </h2>

              <div className="flex flex-col items-center gap-4">
                {/* Image Preview */}
                <div className="relative">
                  {imagePreview ? (
                    <div className="w-32 h-32 rounded-full border-4 border-gray-300 overflow-hidden">
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Upload Buttons */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                    style={{ color: '#ffffff' }}
                  >
                    <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                    Upload Image
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCameraCapture}
                    className={`flex items-center gap-2 ${
                      isCameraActive
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    style={{ color: '#ffffff' }}
                  >
                    <Camera className="w-4 h-4" style={{ color: '#ffffff' }} />
                    Take Photo
                  </Button>
                </div>

                {/* Hidden Canvas for Photo Capture */}
                <canvas
                  ref={canvasRef}
                  className="hidden"
                  width="640"
                  height="480"
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Camera Preview - Positioned directly below the upload buttons section */}
              {showCameraPreview && (
                <div className="w-full mt-6">
                  <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
                    <div className="text-center mb-3">
                      <h3 className="text-lg font-medium text-gray-800 mb-2">
                        Camera Preview
                      </h3>
                      {cameraError && (
                        <p className="text-red-600 text-sm mb-2">
                          {cameraError}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-80 h-60 rounded-lg border border-gray-300 bg-black"
                        />

                        <div className="flex justify-center gap-3 mt-3">
                          <Button
                            type="button"
                            onClick={takePhoto}
                            disabled={isCapturing}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 px-4 py-2"
                          >
                            {isCapturing ? 'Capturing...' : 'Capture Photo'}
                          </Button>

                          <Button
                            type="button"
                            onClick={stopCamera}
                            className="bg-gray-600 hover:bg-gray-700 px-4 py-2"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card.Glassmorphism>

          <Card.Gradient className="p-6">
            <h2 className={`${typography.h5} mb-4 text-black`}>
              Log Details
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-600">Pick Date</label>
                <Input
                  type="date"
                  value={logDate}
                  onChange={(e) => {
                    setLogDate(e.target.value);
                    setFeedback({ success: '', error: '' });
                  }}
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-600">Site</label>
                <select
                  className="h-11 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={selectedSite}
                  onChange={(e) => {
                    setSelectedSite(e.target.value);
                    setFeedback({ success: '', error: '' });
                  }}
                >
                  <option value="">
                    {isSitesLoading ? 'Loading sites...' : 'Select site'}
                  </option>
                  {siteOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="text-sm text-red-600">{sitesError}</p>
                )}
              </div>
            </div>
          </Card.Gradient>

          <Card.Light className="p-6">
            <div className="grid grid-cols-1 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-600">Item Type</label>
                <select
                  className="h-11 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={isStockItem ? 'stock' : 'non-stock'}
                  onChange={(e) => {
                    const isStock = e.target.value === 'stock';
                    setIsStockItem(isStock);
                    setSelectedItem('');
                    setSelectedItemId('');
                    setSelectedItemCurrentQuantity(null);
                    setProductName('');
                    setQuantity('');
                    setFeedback({ success: '', error: '' });
                  }}
                >
                  <option value="stock">Stock Item</option>
                  <option value="non-stock">Non-Stock Item / Product</option>
                </select>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {isStockItem ? (
                  <div className="flex flex-col gap-2">
                    <label className="text-sm text-gray-600">Stock Item</label>
                    <select
                      className="h-11 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={selectedItemId}
                      onChange={(e) => {
                        const itemId = e.target.value;
                        setSelectedItemId(itemId);
                        const selectedItem = stockItems.find(item => item.id === itemId);
                        setSelectedItem(selectedItem?.name || '');
                        setSelectedItemCurrentQuantity(selectedItem?.current_quantity || null);
                        // Reset quantity when item changes
                        setQuantity('');
                        setFeedback({ success: '', error: '' });
                      }}
                      disabled={isStockItemsLoading}
                    >
                      <option value="">
                        {isStockItemsLoading ? 'Loading items...' : 'Select stock item'}
                      </option>
                      {stockItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {stockItemsError && (
                      <p className="text-sm text-red-600">{stockItemsError}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="text-sm text-gray-600">Product Name</label>
                    <Input
                      type="text"
                      value={productName}
                      onChange={(e) => {
                        setProductName(e.target.value);
                        setSelectedItem(e.target.value);
                        setFeedback({ success: '', error: '' });
                      }}
                      placeholder="Enter product or service name"
                      className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                    />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-gray-600">
                    Quantity
                    {isStockItem && selectedItemCurrentQuantity !== null && (
                      <span className="ml-2 text-xs text-gray-500 font-normal">
                        (Available: {selectedItemCurrentQuantity})
                      </span>
                    )}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max={isStockItem && selectedItemCurrentQuantity !== null ? selectedItemCurrentQuantity : undefined}
                    step="1"
                    value={quantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      
                      // Only allow whole numbers (no decimals)
                      if (value !== '' && value.includes('.')) {
                        // Remove decimal point and everything after it
                        const intValue = value.split('.')[0];
                        if (intValue === '') {
                          setQuantity('');
                          return;
                        }
                        setQuantity(intValue);
                        return;
                      }
                      
                      // Allow empty string or valid integer
                      if (value === '' || /^\d+$/.test(value)) {
                        const numValue = parseInt(value, 10);
                        
                        // Validate against current quantity for stock items
                        if (isStockItem && selectedItemCurrentQuantity !== null && value !== '' && !isNaN(numValue)) {
                          if (numValue > selectedItemCurrentQuantity) {
                            setFeedback({ 
                              success: '', 
                              error: `Quantity cannot exceed available stock (${selectedItemCurrentQuantity})` 
                            });
                            return;
                          }
                        }
                        
                        setQuantity(value);
                        setFeedback({ success: '', error: '' });
                      }
                    }}
                    placeholder="0"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-600">Reason</label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setFeedback({ success: '', error: '' });
                  }}
                  placeholder="Describe the reason (e.g., damaged goods, process issue, etc.)"
                  className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-gray-600">
                    Cost per kg/unit (optional)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={itemCost}
                    onChange={(e) => {
                      setItemCost(e.target.value);
                      setFeedback({ success: '', error: '' });
                    }}
                    placeholder="0.00"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-gray-600">Category</label>
                  <select
                    className="h-11 rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setFeedback({ success: '', error: '' });
                    }}
                  >
                    <option value="">Select category</option>
                    {categoryOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm text-gray-600">
                  Est. Loss due to waste
                </label>
                <Input
                  type="number"
                  value={estimatedLoss.toFixed(2)}
                  readOnly
                  className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-900"
                />
              </div>
            </div>
          </Card.Light>

          {feedback.error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {feedback.error}
            </div>
          )}
          {feedback.success && (
            <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {feedback.success}
            </div>
          )}

          <div className="flex justify-end">
            <PrimaryButton type="submit" disabled={!isFormValid || isSubmitting}>
              {isSubmitting 
                ? (isEditMode ? 'Updating...' : 'Saving...') 
                : (isEditMode ? 'Update' : 'Submit')}
            </PrimaryButton>
          </div>
        </form>
      </div>
  
  );
};

export default WasteLogging;

